import React, { useEffect, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { waitForAngular } from 'App/AngularApp';
import Page from 'App/pages/Page/Page';

type PortedRouteConfig = {
	angularState: string;
	featureFlag: string;
};

const portedRoutes = [
	{
		angularState: 'looker',
		featureFlag: 'PORTED_ROUTE_LOOKER'
	} as const,
	{
		angularState: 'oauthLanding',
		featureFlag: 'PORTED_ROUTE_OAUTH_LANDING'
	} as const,
	{
		angularState: 'mailCampaign',
		featureFlag: 'REACT_MAIL_CAMPAIGN'
	} as const,
	{
		angularState: 'mailCampaign.dashboard',
		featureFlag: 'REACT_MAIL_CAMPAIGN'
	} as const,
	{
		angularState: 'mailCampaign.visits',
		featureFlag: 'REACT_MAIL_CAMPAIGN'
	} as const,
	{
		angularState: 'mailCampaign.submits',
		featureFlag: 'REACT_MAIL_CAMPAIGN'
	} as const,
	{
		angularState: 'mailCampaign.recipients',
		featureFlag: 'REACT_MAIL_CAMPAIGN'
	} as const,
	{
		angularState: 'manageAccount',
		featureFlag: 'PORTED_ROUTE_MANAGE_ACCOUNT'
	} as const,
	{
		angularState: 'flow',
		featureFlag: 'PORT_EDIT_FLOW'
	} as const,
	{
		angularState: 'editFlow',
		featureFlag: 'PORT_FLOW_EDITOR'
	} as const,
	{
		angularState: 'editFlowStandalone',
		featureFlag: 'PORT_FLOW_EDITOR'
	} as const,
	{
		angularState: 'landingpageEditor',
		featureFlag: 'PORTED_LANDINGPAGE_EDITOR_ROUTE_AND_MODAL'
	} as const,
	{
		angularState: 'administration.imports',
		featureFlag: 'PORTED_ROUTE_IMPORTS'
	} as const,
	{
		angularState: 'administration.import',
		featureFlag: 'REACT_IMPORT'
	} as const,
	{
		angularState: 'administration.integration',
		featureFlag: 'REACT_ADMIN_CONFIGURE_INTEGRATION'
	} as const,
	{
		angularState: 'findProspects',
		featureFlag: 'PORTED_ROUTE_FIND_PROSPECTS'
	} as const,
	{
		angularState: 'administration.user',
		featureFlag: 'PORTED_ROUTE_USER'
	} as const
] satisfies PortedRouteConfig[];

const getFeatureFlag = (angularState: string) => {
	const portedRoute = portedRoutes.find(route => route.angularState === angularState);
	return portedRoute?.featureFlag || '';
};

export const setupPortedAngularStates = () => {
	for (const route of portedRoutes) {
		if (Tools.FeatureHelper.hasSoftDeployAccess(route.featureFlag)) {
			// Haxxing to make angular state not do anything
			// Resolves cannot be disabled like this - need to be handled separately
			const oldState = Tools.$state.get(route.angularState) as any;

			delete oldState.controller;
			delete oldState.controllerProvider;
			delete oldState.templateProvider;
			delete oldState.templateUrl;

			oldState.template = '<div></div>';
		}
	}
};

export const checkPortedResolve = async <TReturn extends any>(angularState: string, resolveFn: () => TReturn) => {
	const flag = getFeatureFlag(angularState);
	await waitForAngular();
	return Tools.AppService.loadedPromise.then(() => {
		if (Tools.FeatureHelper.hasSoftDeployAccess(flag)) {
			return;
		}
		return resolveFn();
	});
};

export const reactRouteCompatibility = (
	Component: React.ComponentType<any>,
	{
		featureFlag,
		// probably needs to be more advanced idk it'll do for now
		wrapInPage = true,
		pageProps = {},
		onMount,
		getKey
	}: {
		featureFlag: string;
		wrapInPage?: boolean;
		pageProps?: { [key: string]: any };
		/**
		 * Use to include side effects or data from resolve functions
		 * @return {object} Object with props to pass to the component
		 */
		onMount?: ({ params, query }: { params: any; query: URLSearchParams }) => object | void;
		getKey?: ({ params, query }: { params: any; query: URLSearchParams }) => string;
	}
) => {
	return (props: any) => {
		const hasFeatureFlag = Tools.FeatureHelper.hasSoftDeployAccess(featureFlag);
		const [resolvedProps, setResolvedProps] = useState<any>(null);
		const [key, setKey] = useState<string | undefined>(undefined);

		const { params } = useRouteMatch();
		const { search } = useLocation();

		const query = new URLSearchParams(search);

		useEffect(() => {
			if (hasFeatureFlag && onMount) {
				const resolved = onMount({ params, query });
				setResolvedProps(resolved ?? {});
			}
			if (getKey) {
				setKey(getKey({ params, query }));
			}
		}, [params, search]);

		// No feature flag, no render.
		if (!hasFeatureFlag) {
			return null;
		}

		if (onMount && !resolvedProps) {
			return null;
		}

		return wrapInPage ? (
			<Page {...pageProps}>
				<Component key={key} {...props} {...resolvedProps} />
			</Page>
		) : (
			<Component key={key} {...props} {...resolvedProps} />
		);
	};
};
